import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

const POLL_MS = 3 * 60 * 1000;       // every 3 minutes
const INITIAL_DELAY_MS = 30 * 1000;  // skip the first 30s so a slow first-load doesn't false-positive

// Polls the live index.html ETag and surfaces a "new version available"
// banner the moment a fresh deploy lands. Without this, a freshly
// deployed bundle only reaches users on their next full page nav (or
// after the 60-second cache TTL on index.html) — operators who keep
// the BBB tab open for hours during a match would miss every mid-day
// hotfix. Now: the banner shows up automatically, one click reloads
// the page and they're on the new code.
//
// Implementation: HEAD `/` on a timer; compare ETag (or Last-Modified
// as fallback) to whatever we saw on mount. The bundle that's currently
// running treats its own loaded version as the baseline.
export default function VersionWatcher() {
  const [baseline, setBaseline] = useState(null); // {etag, lastModified} | null
  const [latest, setLatest] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Skip when the page wasn't served via HTTP(S) — e.g. file:// during
  // local debugging or the Electron client. Polling the document root
  // there returns nothing useful and just spams console errors.
  const canPoll = typeof window !== "undefined"
    && window.location?.protocol?.startsWith("http");

  useEffect(() => {
    if (!canPoll) return undefined;
    let alive = true;

    const probe = async () => {
      try {
        const res = await fetch("/index.html", {
          method: "HEAD",
          cache: "no-store",
        });
        if (!alive) return null;
        const etag = res.headers.get("etag") || "";
        const lastModified = res.headers.get("last-modified") || "";
        return { etag, lastModified };
      } catch {
        return null;
      }
    };

    let timer;
    const tick = async () => {
      const sample = await probe();
      if (!alive || !sample) return;
      if (!baseline) {
        setBaseline(sample);
      } else if (
        (sample.etag && sample.etag !== baseline.etag)
        || (!sample.etag && sample.lastModified && sample.lastModified !== baseline.lastModified)
      ) {
        setLatest(sample);
      }
      timer = setTimeout(tick, POLL_MS);
    };

    // Run a baseline probe right away, but defer the comparison loop
    // by INITIAL_DELAY_MS so the very first probe (which is also the
    // baseline) doesn't immediately fire the banner.
    probe().then((s) => { if (alive && s) setBaseline(s); });
    timer = setTimeout(tick, INITIAL_DELAY_MS);

    // Re-check when the tab regains focus — a backgrounded BBB tab
    // shouldn't have to wait 3 minutes after the user comes back to
    // discover a hotfix landed.
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPoll]);

  if (!latest || dismissed) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[90] flex items-center gap-2 rounded-lg border border-emerald-500/50 bg-emerald-500/15 text-emerald-100 backdrop-blur-sm shadow-xl px-3 py-2 text-sm"
      style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <RefreshCw className="w-4 h-4" />
      <span>A new version of ZenCricketData is available.</span>
      <button
        onClick={() => window.location.reload()}
        className="ml-2 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded bg-emerald-500 text-bg-main hover:bg-emerald-400"
      >
        Refresh
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-emerald-200 hover:text-white opacity-70 hover:opacity-100"
        title="Dismiss (you'll see this again when the next version ships)"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
